import { useState } from 'react';
import { makeStyles } from '@material-ui/core';
import FileCopyOutlined from '@material-ui/icons/FileCopyOutlined';
import { DevHubTooltip } from '@devxdiscover/devhub-ui';

/**
 * Note: This component uses Material-UI's makeStyles and FileCopyOutlined icon
 * instead of DevHub UI components because:
 * - DevHubDynamicIcon does not render properly inside plain HTML button elements
 * - We need a plain button (not DevHubIconButton) to maintain compact inline layout
 * - makeStyles provides precise control over button styling for inline use
 */
const useStyles = makeStyles(theme => ({
  copyButton: {
    padding: 0,
    marginLeft: 4,
    minWidth: 'auto',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    verticalAlign: 'middle',
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
}));

interface CopyIdButtonProps {
  value: string | number;
  fontSize?: string;
  ariaLabel?: string;
}

/**
 * A compact copy-to-clipboard button for inline use with application IDs.
 * Displays a copy icon that shows a tooltip feedback when clicked.
 *
 * @param props - Component props
 */
export const CopyIdButton = ({
  value,
  fontSize = '0.875rem',
  ariaLabel = 'Copy application ID',
}: CopyIdButtonProps) => {
  const classes = useStyles();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value.toString());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to copy:', err);
    }
  };

  return (
    <DevHubTooltip title={copied ? 'Copied!' : 'Copy ID'}>
      <button
        className={classes.copyButton}
        onClick={handleCopy}
        aria-label={ariaLabel}
        style={{ fontSize }}
      >
        <FileCopyOutlined style={{ fontSize }} />
      </button>
    </DevHubTooltip>
  );
};
